/* eslint-disable react-hooks/rules-of-hooks */
import { useMemo } from 'react';

import type { FEATURE } from '~/common/enums/feature.enum';
import {
  getStaticTreatment,
  isMixpanelFlag,
  isStaticFlag,
} from '~/domains/feature-flags/featureFlagSource';
import { useMixpanelTreatments } from '~/domains/feature-flags/mixpanel/useMixpanelTreatments';
import type { TreatmentWithConfig } from '~/domains/feature-flags/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useTreatments = <F extends FEATURE, FC extends Record<F, any>>(
  featureFlagNames: F[]
): {
  isReady: boolean;
  /**
   * Ready, ready from cache, or timed out: the treatments are an answer you can act on now
   * (a timed-out flag reads as `control`, i.e. off). They may still update later.
   */
  isSettled: boolean;
  treatments: {
    [key in F]: TreatmentWithConfig<FC[key]> | null;
  };
} => {
  if (process.env.STORYBOOK) {
    return {
      isReady: true,
      isSettled: true,
      treatments: featureFlagNames.reduce(
        (acc, featureFlagName) => {
          acc[featureFlagName] = {
            treatment: 'on',
            config: null,
          };

          return acc;
        },
        {} as Record<F, TreatmentWithConfig>
        // The generic FC[key] can't be proven from a concrete object at this call site.
      ) as unknown as { [key in F]: TreatmentWithConfig<FC[key]> | null },
    };
  }

  const namesKey = featureFlagNames.join('|');
  const [staticTreatments, mixpanelNames] = useMemo(
    () =>
      [
        Object.fromEntries(
          featureFlagNames.flatMap((name) =>
            isStaticFlag(name) ? [[name, getStaticTreatment(name)] as const] : []
          )
        ),
        featureFlagNames.filter(isMixpanelFlag),
      ] as const,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the names, not the array identity
    [namesKey]
  );
  // Hardcoded flags are always settled, so only Mixpanel flags can hold a read back.
  const mixpanel = useMixpanelTreatments(mixpanelNames);
  const treatments = useMemo(
    () => ({ ...staticTreatments, ...mixpanel.treatments }),
    [staticTreatments, mixpanel.treatments]
  );

  return {
    isReady: !mixpanelNames.length || mixpanel.isReady,
    isSettled: !mixpanelNames.length || mixpanel.isSettled,
    // Same generic-can't-be-proven situation as the STORYBOOK branch above.
    treatments: treatments as unknown as { [key in F]: TreatmentWithConfig<FC[key]> | null },
  };
};
